import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  mockSyncPathConfigs,
  SyncPathConfig,
  SelectedFolder,
  MOCK_STUDIES,
} from "@/mocks/documentsData";
import { FilterDropdown, DropdownOption } from "../../../documents/components/DocShared";
import NewStudyModal from "@/pages/admin/studies/components/NewStudyModal";
import StudyCodeSelect from "@/pages/admin/studies/components/StudyCodeSelect";
import { mockStudies, type Study } from "@/mocks/studiesData";
import { createStudy } from "@/services/studies";
import AdminLayout from "../../../../components/layout/AdminLayout";
import AdminPageTitle from "@/components/admin/AdminPageTitle";
import FolderExpandableItem from "../components/FolderExpandableItem";
import MVRSourceFormPage, { type MVRSourceFormData } from "../components/MVRSourceFormPage";

// ── URL Modal ──────────────────────────────────────────────────────────────────
function UrlModal({ url, isZh, onClose }: { url: string; isZh: boolean; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100">
              <i className="ri-links-line text-sm text-gray-500"></i>
            </div>
            <h3 className="text-sm font-semibold text-gray-900">Source URL</h3>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-400 text-base"></i>
          </button>
        </div>
        <div className="bg-gray-50 rounded-xl px-4 py-3.5 mb-4 border border-gray-100">
          <p className="text-xs font-mono text-gray-700 break-all leading-relaxed select-all">
            {url}
          </p>
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className={copied ? "ri-check-line text-green-600" : "ri-file-copy-line"}></i>
            {copied ? (isZh ? "已复制" : "Copied!") : isZh ? "复制链接" : "Copy URL"}
          </button>
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-colors cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: "#830051" }}
          >
            {isZh ? "关闭" : "Close"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Delete Confirm Modal ───────────────────────────────────────────────────────
function DeleteConfirmModal({
  isZh,
  sourceName,
  onConfirm,
  onCancel,
}: {
  isZh: boolean;
  sourceName: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl p-6 w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-50 shrink-0">
            <i className="ri-error-warning-line text-lg text-red-500"></i>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              {isZh ? "确认删除" : "Confirm Delete"}
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              {isZh ? "此操作不可撤销" : "This action cannot be undone"}
            </p>
          </div>
        </div>
        <p className="text-xs text-gray-600 leading-relaxed mb-6">
          {isZh
            ? `确定要删除 Source「${sourceName}」吗？删除后该 Source 及其所有配置路径将不可恢复。`
            : `Are you sure you want to delete "${sourceName}"? This source and all configured paths will be permanently removed.`}
        </p>
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-xs font-medium border border-gray-200 text-gray-500 cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors"
          >
            {t("common.cancel")}
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 rounded-lg text-xs font-medium text-white bg-red-500 cursor-pointer whitespace-nowrap hover:bg-red-600 transition-colors"
          >
            {isZh ? "删除" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Global Keywords Modal ─────────────────────────────────────────────────────
function GlobalKeywordsModal({
  isZh,
  initialKeywords,
  onConfirm,
  onCancel,
}: {
  isZh: boolean;
  initialKeywords: string[];
  onConfirm: (keywords: string[]) => void;
  onCancel: () => void;
}) {
  const [keywords, setKeywords] = useState<string[]>([...initialKeywords]);
  const [newKeyword, setNewKeyword] = useState("");

  const addKeyword = () => {
    const raw = newKeyword.trim();
    if (!raw) return;
    const parts = raw
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const newParts = parts.filter((p) => !keywords.includes(p));
    if (newParts.length > 0) {
      setKeywords((prev) => [...prev, ...newParts]);
    }
    setNewKeyword("");
  };

  const removeKeyword = (idx: number) => {
    setKeywords((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-xl shadow-sm w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-50">
          <div
            className="w-8 h-8 flex items-center justify-center rounded-lg"
            style={{ backgroundColor: "rgba(131,0,81,0.08)" }}
          >
            <i className="ri-global-line text-base" style={{ color: "#830051" }}></i>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              {isZh ? "全局排除关键词" : "Global Excluded Keywords"}
            </h3>
          </div>
        </div>

        <div className="px-6 py-5 flex flex-col gap-4">
          {keywords.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {keywords.map((kw, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-1 text-xs text-gray-700 bg-white rounded-md px-2.5 py-1 border border-gray-200"
                >
                  {kw}
                  <button
                    onClick={() => removeKeyword(idx)}
                    className="w-4 h-4 flex items-center justify-center rounded hover:bg-red-50 cursor-pointer flex-shrink-0"
                  >
                    <i className="ri-close-line text-[10px] text-red-400"></i>
                  </button>
                </span>
              ))}
            </div>
          )}
          {keywords.length === 0 && (
            <p className="text-xs text-gray-400 italic">
              {isZh ? "暂无关键词" : "No keywords yet"}
            </p>
          )}

          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addKeyword();
                }
              }}
              placeholder={isZh ? "输入关键词，按回车添加" : "Enter keyword, press Enter to add"}
              className="flex-1 max-w-[320px] text-xs border border-gray-200 rounded-lg px-3 py-1.5 outline-none focus:border-gray-400 bg-white"
            />
            <button
              onClick={addKeyword}
              disabled={!newKeyword.trim()}
              className="text-xs px-3 py-1.5 rounded-lg text-white cursor-pointer whitespace-nowrap transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ backgroundColor: "#830051" }}
            >
              {isZh ? "添加" : "Add"}
            </button>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 sticky bottom-0 bg-white">
          <button
            onClick={onCancel}
            className="text-xs px-4 py-1.5 rounded-lg border border-gray-200 text-gray-500 cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors"
          >
            {isZh ? "取消" : "Cancel"}
          </button>
          <button
            onClick={() => onConfirm(keywords)}
            className="text-xs px-4 py-1.5 rounded-lg text-white cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: "#830051" }}
          >
            {isZh ? "保存" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Folder Status Helper for summary ──────────────────────────────────────────
function getFolderStatusSummary(folders: SelectedFolder[]) {
  const total = folders.length;
  const unavailable = folders.filter((f) => f.folderStatus === "unavailable").length;
  const active = total - unavailable;
  return { total, active, unavailable };
}

// ── Add/Edit Source Modal (no validation inside modal) ────────────────────────
interface ChannelForm {
  studyCode: string;
  studyName: string;
  channelUrl: string;
}

function ChannelModal({
  isZh,
  editingSource,
  onConfirm,
  onCancel,
}: {
  isZh: boolean;
  editingSource: SyncPathConfig | null;
  onConfirm: (config: {
    form: ChannelForm;
    folderPaths: { path: string; exclusions: string[] }[];
  }) => void;
  onCancel: () => void;
}) {
  const isEdit = editingSource != null;

  const [form, setForm] = useState<ChannelForm>({
    studyCode: editingSource?.studyCode ?? "",
    studyName: editingSource?.studyName ?? "",
    channelUrl: editingSource?.channelUrl ?? "",
  });

  const initialRows =
    editingSource && editingSource.selectedFolders.length > 0
      ? editingSource.selectedFolders.map((f, i) => ({
          id: i,
          path: f.name,
          exclusions: f.exclusions?.length ? f.exclusions : [""],
        }))
      : [{ id: 0, path: "", exclusions: [""] }];
  const [pathRows, setPathRows] =
    useState<{ id: number; path: string; exclusions: string[] }[]>(initialRows);
  const nextIdRef = useRef(
    editingSource && editingSource.selectedFolders.length > 0
      ? editingSource.selectedFolders.length
      : 1,
  );
  const [showNewStudy, setShowNewStudy] = useState(false);

  const addPathRow = () => {
    const id = nextIdRef.current;
    nextIdRef.current += 1;
    setPathRows((prev) => [...prev, { id, path: "", exclusions: [""] }]);
  };

  const removePathRow = (id: number) => {
    setPathRows((prev) => prev.filter((r) => r.id !== id));
  };

  const updatePathRow = (id: number, path: string) => {
    setPathRows((prev) => prev.map((r) => (r.id === id ? { ...r, path } : r)));
  };

  const addExclusionRow = (pathId: number) => {
    setPathRows((prev) =>
      prev.map((r) => (r.id === pathId ? { ...r, exclusions: [...r.exclusions, ""] } : r)),
    );
  };

  const removeExclusionRow = (pathId: number, exclusionIdx: number) => {
    setPathRows((prev) =>
      prev.map((r) =>
        r.id === pathId
          ? { ...r, exclusions: r.exclusions.filter((_, i) => i !== exclusionIdx) }
          : r,
      ),
    );
  };

  const updateExclusionRow = (pathId: number, exclusionIdx: number, value: string) => {
    setPathRows((prev) =>
      prev.map((r) =>
        r.id === pathId
          ? { ...r, exclusions: r.exclusions.map((e, i) => (i === exclusionIdx ? value : e)) }
          : r,
      ),
    );
  };

  const filledPaths = pathRows.filter((r) => r.path.trim().length > 0);
  const canConfirm =
    form.studyCode.trim().length > 0 && form.channelUrl.trim().length > 0 && filledPaths.length > 0;

  const handleConfirm = () => {
    const validPaths = filledPaths.map((r) => ({
      path: r.path.trim(),
      exclusions: r.exclusions.map((e) => e.trim()).filter((e) => e.length > 0),
    }));
    if (validPaths.length === 0) return;
    const study = mockStudies.find((s) => s.code === form.studyCode);
    const resolvedName = study?.name ?? form.studyCode;
    onConfirm({ form: { ...form, studyName: resolvedName }, folderPaths: validPaths });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-xl mx-4 overflow-hidden flex flex-col"
        style={{ maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0"
              style={{ backgroundColor: "rgba(131,0,81,0.08)" }}
            >
              <i
                className={`${isEdit ? "ri-pencil-line" : "ri-links-line"} text-base`}
                style={{ color: "#830051" }}
              ></i>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                {isEdit
                  ? isZh
                    ? "编辑 Source"
                    : "Edit Source"
                  : isZh
                    ? "添加 Source"
                    : "Add Source"}
              </h3>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-close-line text-gray-400 text-base"></i>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 flex flex-col gap-5 overflow-y-auto flex-1">
          {/* AZ Auth notice */}
          <div
            className="flex items-start gap-3 px-3.5 py-3 rounded-xl border"
            style={{ backgroundColor: "rgba(131,0,81,0.04)", borderColor: "rgba(131,0,81,0.15)" }}
          >
            <div
              className="w-6 h-6 flex items-center justify-center rounded-lg shrink-0 mt-0.5"
              style={{ backgroundColor: "rgba(131,0,81,0.08)" }}
            >
              <i className="ri-shield-keyhole-line text-xs" style={{ color: "#830051" }}></i>
            </div>
            <div>
              <p className="text-xs font-semibold" style={{ color: "#830051" }}>
                {isZh ? "需要 AZ IT 授权" : "AZ IT Authorization Required"}
              </p>
              <p
                className="text-xs mt-0.5 leading-relaxed"
                style={{ color: "rgba(131,0,81,0.75)" }}
              >
                {isZh
                  ? "该 Source 必须由 AZ IT 对系统 client_id 完成授权，否则文件夹内容无法被索引。"
                  : "The source must be authorized by AZ IT for this system's client_id before folders can be indexed."}
              </p>
            </div>
          </div>

          {/* Study Select */}
          <div>
            <label className="text-xs font-medium text-gray-700">
              {isZh ? "Study" : "Study"} <span className="text-red-400">*</span>
            </label>
            <div className="mt-1.5">
              <StudyCodeSelect
                value={form.studyCode}
                studyName={form.studyName}
                onChange={(code) => {
                  const study = mockStudies.find((s) => s.code === code);
                  setForm((f) => ({ ...f, studyCode: code, studyName: study?.name ?? code }));
                }}
                onNewStudy={() => setShowNewStudy(true)}
                hasError={!form.studyCode.trim()}
              />
            </div>
          </div>

          {/* Channel URL */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-gray-700">
              {isZh ? "Source 链接" : "Source URL"} <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={form.channelUrl}
              onChange={(e) => setForm((f) => ({ ...f, channelUrl: e.target.value }))}
              placeholder="https://teams.microsoft.com/l/channel/..."
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-gray-400 font-mono"
            />
            <p className="text-[10px] text-gray-400 leading-relaxed">
              {isZh
                ? "在 Teams 中右键点击 Channel \u2192 「获取 Channel 链接」即可复制，或直接填入 SharePoint Site URL"
                : 'In Teams, right-click the Channel \u2192 "Get link to channel" to copy, or paste a SharePoint Site URL directly'}
            </p>
          </div>

          {/* Folder Paths */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-gray-700">
                {isZh ? "同步文件夹路径" : "Sync Folder Paths"}{" "}
                <span className="text-red-400">*</span>
              </label>
              <button
                onClick={addPathRow}
                className="flex items-center gap-1 text-xs font-medium cursor-pointer transition-colors hover:opacity-80"
                style={{ color: "#830051" }}
              >
                <i className="ri-add-line"></i>
                {isZh ? "添加路径" : "Add Path"}
              </button>
            </div>

            <div className="flex flex-col gap-2">
              {pathRows.map((row) => (
                <div key={row.id} className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 h-6 flex items-center justify-center pointer-events-none">
                        <span className="text-sm text-gray-300 font-mono">/</span>
                      </div>
                      <input
                        type="text"
                        value={row.path}
                        onChange={(e) => updatePathRow(row.id, e.target.value)}
                        placeholder={isZh ? "例如：01_Protocol/CVR" : "e.g. 01_Protocol/CVR"}
                        className="w-full pl-6 pr-8 py-2 text-sm border border-gray-200 rounded-lg outline-none font-mono focus:border-gray-400"
                      />
                    </div>
                    {pathRows.length > 1 && (
                      <button
                        onClick={() => removePathRow(row.id)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors cursor-pointer shrink-0"
                        title={isZh ? "删除" : "Remove"}
                      >
                        <i className="ri-delete-bin-line text-xs text-red-400"></i>
                      </button>
                    )}
                  </div>
                  {/* Excluded Subfolders */}
                  <div className="border border-gray-200 rounded-lg bg-gray-50/50 p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-folder-forbid-line text-xs text-gray-400"></i>
                      <span className="text-xs font-medium text-gray-700">Excluded Subfolders</span>
                      <span className="text-[10px] text-gray-400">(optional)</span>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      {row.exclusions.map((exclusion, exIdx) => (
                        <div key={exIdx} className="flex items-center gap-2">
                          <div className="flex-1 relative">
                            <input
                              type="text"
                              value={exclusion}
                              onChange={(e) => updateExclusionRow(row.id, exIdx, e.target.value)}
                              placeholder={isZh ? "/folder/to/exclude/" : "/folder/to/exclude/"}
                              className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg outline-none font-mono focus:border-gray-400 bg-white text-gray-600"
                            />
                          </div>
                          {row.exclusions.length > 1 && (
                            <button
                              onClick={() => removeExclusionRow(row.id, exIdx)}
                              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors cursor-pointer shrink-0"
                              title={isZh ? "移除" : "Remove"}
                            >
                              <i className="ri-close-line text-xs text-gray-400 hover:text-red-400"></i>
                            </button>
                          )}
                          {exIdx === row.exclusions.length - 1 && (
                            <button
                              onClick={() => addExclusionRow(row.id)}
                              className="flex items-center gap-1 text-xs font-medium text-white cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90 px-3 py-1.5 rounded-lg shrink-0"
                              style={{ backgroundColor: "#830051" }}
                            >
                              <i className="ri-add-line text-xs"></i>
                              {isZh ? "Add Exclusion" : "Add Exclusion"}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}

              {/* Path examples */}
              <div className="flex items-start gap-2 mt-1 px-1">
                <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-lightbulb-line text-xs text-gray-300"></i>
                </div>
                <div className="flex flex-col gap-1.5">
                  <span className="text-[10px] text-gray-400 font-medium">
                    {isZh ? "路径示例：" : "Path examples:"}
                  </span>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] text-gray-500 bg-gray-50 border border-gray-100 rounded-md px-2 py-0.5 font-mono whitespace-nowrap">
                      /01_Protocol
                    </span>
                    <span className="text-[10px] text-gray-500 bg-gray-50 border border-gray-100 rounded-md px-2 py-0.5 font-mono whitespace-nowrap">
                      /02_ICF
                    </span>
                    <span className="text-[10px] text-gray-500 bg-gray-50 border border-gray-100 rounded-md px-2 py-0.5 font-mono whitespace-nowrap">
                      /03_Regulatory/Approvals
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 shrink-0">
          <button
            onClick={onCancel}
            className="text-xs px-4 py-1.5 rounded-lg border border-gray-200 text-gray-500 cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors"
          >
            {isZh ? "取消" : "Cancel"}
          </button>

          <button
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="flex items-center gap-1.5 text-xs px-4 py-1.5 rounded-lg text-white cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: "#830051" }}
          >
            <i className="ri-check-line"></i>
            {isEdit
              ? isZh
                ? "保存修改"
                : "Save Changes"
              : isZh
                ? `确认添加 (${filledPaths.length} 个文件夹)`
                : `Import (${filledPaths.length} folder${filledPaths.length > 1 ? "s" : ""})`}
          </button>
        </div>
      </div>

      {/* New Study Modal */}
      {showNewStudy && (
        <NewStudyModal
          onClose={() => setShowNewStudy(false)}
          onSave={async (input) => {
            const study = await createStudy(input);
            mockStudies.push({
              ...study,
              cra: study.assignedCras.map((user) => user.name),
              status: study.status as Study["status"],
            });
            setForm((f) => ({ ...f, studyCode: study.code }));
            setShowNewStudy(false);
          }}
        />
      )}
    </div>
  );
}

// ── Async path validator (simulated) ────────────────────────────────────────────
function runAsyncPathValidation(
  sourceId: number,
  folders: SelectedFolder[],
  onUpdate: (
    id: number,
    updater: SelectedFolder[] | ((prev: SelectedFolder[]) => SelectedFolder[]),
  ) => void,
) {
  // Set all folders to validating
  const validatingFolders = folders.map((f) => ({
    ...f,
    pathValidationStatus: "validating" as const,
    pathValidationError: undefined,
  }));
  onUpdate(sourceId, validatingFolders);

  // Simulate per-folder async validation (5-15s per folder, staggered)
  folders.forEach((folder, idx) => {
    const delay = 3000 + Math.random() * 8000 + idx * 1500;
    setTimeout(() => {
      // 80% valid, 20% invalid
      const isValid = Math.random() > 0.2;
      const updated: SelectedFolder = {
        ...folder,
        pathValidationStatus: isValid ? "valid" : "invalid",
        pathValidationError: isValid ? undefined : "Folder path not found on SharePoint",
      };
      onUpdate(sourceId, (prev) => prev.map((f) => (f.id === folder.id ? updated : f)));
    }, delay);
  });
}

// ── Connection Badge ──────────────────────────────────────────────────────────
function ConnectionBadge({
  type,
  isZh,
}: {
  type: "teams" | "sharepoint" | undefined;
  isZh: boolean;
}) {
  const isTeams = type === "teams";
  if (!type) return <span className="text-xs text-gray-300">—</span>;
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
      style={{
        backgroundColor: isTeams ? "rgba(131,0,81,0.06)" : "rgba(37,99,235,0.06)",
        color: isTeams ? "#830051" : "#2563eb",
        border: `1px solid ${isTeams ? "rgba(131,0,81,0.12)" : "rgba(37,99,235,0.12)"}`,
      }}
    >
      <i className={isTeams ? "ri-team-line" : "ri-cloud-line"}></i>
      {isTeams ? (isZh ? "Teams" : "Teams") : isZh ? "SharePoint" : "SharePoint"}
    </span>
  );
}

function getUniqueExclusions(path: SyncPathConfig): string[] {
  return [...new Set(path.selectedFolders.flatMap((f) => f.exclusions ?? []))];
}

// ── Row ───────────────────────────────────────────────────────────────────────
function Row({
  path,
  isZh,
  onViewUrl,
  onEdit,
  onDelete,
}: {
  path: SyncPathConfig;
  isZh: boolean;
  onViewUrl: (url: string) => void;
  onEdit: (path: SyncPathConfig) => void;
  onDelete: (path: SyncPathConfig) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [isIncludeExpanded, setIsIncludeExpanded] = useState(false);
  const [isExcludeExpanded, setIsExcludeExpanded] = useState(false);
  const includeNames = path.selectedFolders.map((f) => f.name);
  const excludeNames = getUniqueExclusions(path);
  const hasFolders = path.selectedFolders && path.selectedFolders.length > 0;

  const visibleIncludes = includeNames.slice(0, 2);
  const hiddenIncludes = includeNames.slice(2);
  const visibleExcludes = excludeNames.slice(0, 2);
  const hiddenExcludes = excludeNames.slice(2);

  return (
    <div className="border-b border-gray-50 last:border-0">
      <div className="flex items-start gap-4 px-5 py-3 hover:bg-gray-50 transition-colors group">
        {/* Study Name */}
        <div className="flex-none w-[280px] pt-0.5">
          <div className="flex items-center gap-2 min-w-0 relative">
            <button
              onClick={() => hasFolders && setExpanded((v) => !v)}
              className="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-200 transition-colors cursor-pointer shrink-0"
              disabled={!hasFolders}
              style={{ opacity: hasFolders ? 1 : 0.3 }}
              title={isZh ? "展开/收起已配置路径" : "Expand/collapse configured paths"}
            >
              <i
                className="ri-arrow-down-s-line text-sm text-gray-400 transition-transform"
                style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}
              ></i>
            </button>
            <div
              className="w-6 h-6 flex items-center justify-center rounded-md shrink-0"
              style={{ backgroundColor: "rgba(131,0,81,0.06)" }}
            >
              <i
                className={`${path.connectionType === "sharepoint" ? "ri-cloud-line" : "ri-team-line"} text-xs`}
                style={{ color: "#830051" }}
              ></i>
            </div>
            <p className="text-xs font-medium text-gray-700 truncate">{path.channelName}</p>
            {path.studyCode ? (
              <span
                className="inline-flex items-center shrink-0 text-[10px] font-mono font-semibold px-2 py-0.5 rounded whitespace-nowrap"
                style={{
                  backgroundColor: "rgba(131,0,81,0.06)",
                  color: "#830051",
                  border: "1px solid rgba(131,0,81,0.15)",
                }}
              >
                {path.studyCode}
              </span>
            ) : (
              <span className="text-[10px] text-gray-300 whitespace-nowrap">—</span>
            )}
            <button
              onClick={() => onViewUrl(path.channelUrl)}
              className="shrink-0 w-5 h-5 flex items-center justify-center rounded-md hover:bg-gray-200 transition-colors cursor-pointer opacity-0 group-hover:opacity-100 ml-auto"
              title={isZh ? "查看完整链接" : "View full URL"}
            >
              <i className="ri-eye-line text-[10px] text-gray-400"></i>
            </button>
          </div>
          {path.description && (
            <p className="text-[11px] text-gray-400 truncate mt-0.5 pl-8">{path.description}</p>
          )}
        </div>

        {/* Connection Type */}
        <div className="flex-none w-[90px] pt-0.5">
          <ConnectionBadge type={path.connectionType} isZh={isZh} />
        </div>

        {/* URL */}
        <div className="flex-none w-[180px] pt-0.5 flex items-center gap-1.5 relative">
          <p className="text-[10px] text-gray-400 font-mono truncate flex-1 min-w-0">
            {path.channelUrl}
          </p>
          <button
            onClick={() => onViewUrl(path.channelUrl)}
            className="shrink-0 w-5 h-5 flex items-center justify-center rounded-md hover:bg-gray-200 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
            title={isZh ? "查看完整链接" : "View full URL"}
          >
            <i className="ri-eye-line text-[10px] text-gray-400"></i>
          </button>
        </div>

        {/* Include Paths */}
        <div className="flex-none w-[180px] pt-0.5">
          {includeNames.length > 0 ? (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1 flex-wrap">
                {(isIncludeExpanded ? includeNames : visibleIncludes).map((name, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center text-[10px] text-gray-600 font-mono bg-gray-100 rounded px-1.5 py-0.5 max-w-[120px] truncate"
                    title={name}
                  >
                    {name}
                  </span>
                ))}
              </div>
              {hiddenIncludes.length > 0 && (
                <button
                  onClick={() => setIsIncludeExpanded((v) => !v)}
                  className="inline-flex items-center gap-0.5 text-[10px] text-gray-500 bg-white border border-gray-200 rounded px-1.5 py-0.5 hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors self-start"
                >
                  {isIncludeExpanded
                    ? isZh
                      ? "收起"
                      : "Show less"
                    : `+${hiddenIncludes.length} more`}
                  <i
                    className={`text-[10px] ${isIncludeExpanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}
                  ></i>
                </button>
              )}
            </div>
          ) : (
            <span className="text-xs text-gray-300">—</span>
          )}
        </div>

        {/* Excluded Paths */}
        <div className="flex-none w-[140px] pt-0.5">
          {excludeNames.length > 0 ? (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1 flex-wrap">
                {(isExcludeExpanded ? excludeNames : visibleExcludes).map((name, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center text-[10px] text-gray-500 font-mono bg-red-50/60 rounded px-1.5 py-0.5 max-w-[100px] truncate"
                    title={name}
                  >
                    {name}
                  </span>
                ))}
              </div>
              {hiddenExcludes.length > 0 && (
                <button
                  onClick={() => setIsExcludeExpanded((v) => !v)}
                  className="inline-flex items-center gap-0.5 text-[10px] text-gray-500 bg-white border border-gray-200 rounded px-1.5 py-0.5 hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors self-start"
                >
                  {isExcludeExpanded
                    ? isZh
                      ? "收起"
                      : "Show less"
                    : `+${hiddenExcludes.length} more`}
                  <i
                    className={`text-[10px] ${isExcludeExpanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}
                  ></i>
                </button>
              )}
            </div>
          ) : (
            <span className="text-xs text-gray-300">—</span>
          )}
        </div>

        {/* Doc Categories */}
        <div className="flex-none w-[140px] pt-0.5">
          {path.docCategories && path.docCategories.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1">
              {path.docCategories.map((cat, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center text-[10px] text-gray-600 bg-gray-100 rounded px-1.5 py-0.5 truncate max-w-[120px]"
                  title={cat}
                >
                  {cat}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-xs text-gray-300">—</span>
          )}
        </div>

        {/* Doc Tags */}
        <div className="flex-none w-[140px] pt-0.5">
          {path.docTagGroups && path.docTagGroups.length > 0 ? (
            <div className="flex flex-col gap-0.5">
              {path.docTagGroups.map((group, idx) => (
                <div key={idx} className="flex flex-wrap items-center gap-1">
                  <span className="text-[10px] text-gray-500 font-medium">{group.tagType}:</span>
                  {group.tags.map((tag, tidx) => (
                    <span
                      key={tidx}
                      className="inline-flex items-center text-[10px] text-gray-600 bg-gray-100 rounded px-1.5 py-0.5"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <span className="text-xs text-gray-300">—</span>
          )}
        </div>

        {/* User List Sync */}
        <div className="flex-none w-[110px] pt-0.5">
          {path.isUserListSource ? (
            <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 rounded px-1.5 py-0.5 whitespace-nowrap border border-amber-100">
              <i className="ri-user-settings-line"></i>
              {isZh ? "用户列表" : "User List"}
            </span>
          ) : (
            <span className="text-xs text-gray-300">—</span>
          )}
        </div>

        {/* User Scope */}
        <div className="flex-none w-[110px] pt-0.5 flex items-center">
          <span
            className="inline-flex items-center text-xs rounded-full px-2 py-0.5 whitespace-nowrap"
            style={
              path.visibleToUsers
                ? {
                    backgroundColor: "rgba(131,0,81,0.08)",
                    color: "#830051",
                    border: "1px solid rgba(131,0,81,0.15)",
                  }
                : { backgroundColor: "#f3f4f6", color: "#9ca3af", border: "1px solid transparent" }
            }
          >
            {path.visibleToUsers ? (isZh ? "可见" : "Visible") : isZh ? "隐藏" : "Hidden"}
          </span>
        </div>

        {/* Actions */}
        <div className="flex-none w-[90px] flex items-center justify-end gap-1 sticky right-0 z-50 self-stretch bg-white group-hover:bg-gray-50 px-2 border-l border-gray-100">
          <button
            onClick={() => onEdit(path)}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
            title={isZh ? "编辑" : "Edit"}
          >
            <i className="ri-pencil-line text-xs text-gray-500"></i>
          </button>
          <button
            onClick={() => onDelete(path)}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors cursor-pointer"
            title={isZh ? "删除" : "Delete"}
          >
            <i className="ri-delete-bin-line text-xs text-red-400"></i>
          </button>
        </div>
      </div>

      {/* Expanded: selected folders */}
      {expanded && hasFolders && (
        <div className="sticky left-0 px-5 pb-3 w-[calc(100vw-320px)] min-w-[560px] max-w-[1100px]">
          <div className="ml-7 border border-gray-100 rounded-xl overflow-hidden bg-gray-50/40">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex items-center gap-2 justify-between">
              <div className="flex items-center gap-2">
                <i className="ri-folder-3-line text-xs text-gray-400"></i>
                <span className="text-xs font-medium text-gray-500">
                  {isZh ? "已配置路径" : "Configured Paths"}
                </span>
                <span className="text-[10px] text-gray-400 ml-1">
                  ({path.selectedFolders.length} {isZh ? "个" : "paths"})
                </span>
              </div>
            </div>
            <div className="divide-y divide-gray-50">
              {path.selectedFolders.map((folder) => (
                <div key={folder.id} className="flex items-center gap-2 px-4 py-2">
                  <i className="ri-folder-3-line text-xs text-amber-500 shrink-0"></i>
                  <span
                    className={`text-xs font-medium shrink-0 ${folder.pathValidationStatus === "invalid" ? "text-gray-300" : "text-gray-700"}`}
                  >
                    {folder.name}
                  </span>

                  {folder.exclusions && folder.exclusions.length > 0 ? (
                    <div className="flex items-center gap-1 min-w-0 flex-1">
                      <span className="text-[10px] text-gray-300 shrink-0 whitespace-nowrap">
                        {isZh ? "排除:" : "Excl:"}
                      </span>
                      {folder.exclusions.map((exclusion, exIdx) => (
                        <span
                          key={exIdx}
                          className="inline-flex items-center text-[10px] text-gray-400 font-mono bg-gray-100/70 rounded px-1.5 py-0.5 max-w-[120px] truncate"
                        >
                          {exclusion}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="flex-1 min-w-0"></div>
                  )}

                  <div className="shrink-0 flex items-center gap-1">
                    {folder.pathValidationStatus === "validating" && (
                      <span className="flex items-center gap-1 text-[10px] text-blue-600 bg-blue-50 px-2 py-0.5 rounded font-medium">
                        <i className="ri-loader-4-line animate-spin"></i>
                        {isZh ? "校验中" : "Validating"}
                      </span>
                    )}
                    {folder.pathValidationStatus === "valid" && (
                      <span className="flex items-center gap-1 text-[10px] text-green-600 bg-green-50 px-2 py-0.5 rounded font-medium">
                        <i className="ri-checkbox-circle-line"></i>
                        {isZh ? "有效" : "Valid"}
                      </span>
                    )}
                    {folder.pathValidationStatus === "invalid" && (
                      <span
                        className="flex items-center gap-1 text-[10px] text-red-600 bg-red-50 px-2 py-0.5 rounded font-medium"
                        title={folder.pathValidationError}
                      >
                        <i className="ri-close-circle-line"></i>
                        {isZh ? "无效" : "Invalid"}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Add Source Dropdown ───────────────────────────────────────────────────────
function AddSourceDropdown({
  isZh,
  onSelect,
}: {
  isZh: boolean;
  onSelect: (type: "teams" | "sharepoint") => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white rounded-lg cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90 shrink-0"
        style={{ backgroundColor: "#830051" }}
      >
        <i className="ri-add-line text-base"></i>
        {isZh ? "添加 Source" : "Add Source"}
        <i
          className={`ri-arrow-down-s-line text-sm ml-0.5 transition-transform ${open ? "rotate-180" : ""}`}
        ></i>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)}></div>
          <div className="absolute right-0 top-full mt-1.5 z-50 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden min-w-[220px]">
            <button
              onClick={() => {
                onSelect("teams");
                setOpen(false);
              }}
              className="w-full flex items-center gap-2.5 px-4 py-3 text-left transition-colors hover:bg-gray-50 cursor-pointer"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[rgba(131,0,81,0.06)] flex-shrink-0">
                <i className="ri-team-line text-sm text-[#830051]"></i>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-800">Teams Channel</p>
                <p className="text-[10px] text-gray-400 mt-0.5">
                  {isZh ? "添加 Teams 文档同步" : "Add Teams document sync"}
                </p>
              </div>
            </button>
            <div className="border-t border-gray-100"></div>
            <button
              onClick={() => {
                onSelect("sharepoint");
                setOpen(false);
              }}
              className="w-full flex items-center gap-2.5 px-4 py-3 text-left transition-colors hover:bg-gray-50 cursor-pointer"
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-50 flex-shrink-0">
                <i className="ri-cloud-line text-sm text-blue-500"></i>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-800">SharePoint Site</p>
                <p className="text-[10px] text-gray-400 mt-0.5">
                  {isZh ? "添加 SharePoint 文档同步" : "Add SharePoint document sync"}
                </p>
              </div>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function TeamsIntegrationPage() {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === "zh";

  const [syncPaths, setSyncPaths] = useState<SyncPathConfig[]>(mockSyncPathConfigs);
  const [filterStudy, setFilterStudy] = useState<string>("all");
  const [modalUrl, setModalUrl] = useState<string | null>(null);
  const [showChannelModal, setShowChannelModal] = useState(false);
  const [editingSource, setEditingSource] = useState<SyncPathConfig | null>(null);
  const [deletingSource, setDeletingSource] = useState<SyncPathConfig | null>(null);
  const [addedToast, setAddedToast] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [globalKeywords, setGlobalKeywords] = useState<string[]>([]);
  const [showGlobalKeywords, setShowGlobalKeywords] = useState(false);
  const [filterConnection, setFilterConnection] = useState<string>("all");
  const [filterDocCategory, setFilterDocCategory] = useState<string>("all");
  const [filterDocTag, setFilterDocTag] = useState<string>("all");
  const [filterUserListSync, setFilterUserListSync] = useState<string>("all");
  const [filterUserScope, setFilterUserScope] = useState<string>("all");
  const [addFormConnection, setAddFormConnection] = useState<"teams" | "sharepoint" | null>(null);

  const allDocCategories = [...new Set(syncPaths.flatMap((p) => p.docCategories ?? []))].sort();
  const allDocTagTypes = [
    ...new Set(syncPaths.flatMap((p) => (p.docTagGroups ?? []).map((g) => g.tagType))),
  ].sort();

  // Helper to update folder validation status for a source
  const updateSourceFolders = (
    sourceId: number,
    updater: SelectedFolder[] | ((prev: SelectedFolder[]) => SelectedFolder[]),
  ) => {
    setSyncPaths((prev) =>
      prev.map((sp) => {
        if (sp.id !== sourceId) return sp;
        const nextFolders = typeof updater === "function" ? updater(sp.selectedFolders) : updater;
        return { ...sp, selectedFolders: nextFolders };
      }),
    );
  };

  const handleAddChannel = ({
    form,
    folderPaths,
  }: {
    form: { studyCode: string; channelUrl: string };
    folderPaths: { path: string; exclusions: string[] }[];
  }) => {
    const folders: SelectedFolder[] = folderPaths.map((p, idx) => ({
      id: `new-sf-${Date.now()}-${idx}`,
      name: p.path,
      exclusions: p.exclusions,
      docCount: 0,
      buildStatus: "pending" as const,
      folderStatus: "active" as const,
      pathValidationStatus: "validating" as const,
    }));

    const study = mockStudies.find((s) => s.code === form.studyCode);
    const resolvedName = study?.name ?? form.studyCode;

    const newPath: SyncPathConfig = {
      id: Date.now(),
      studyCode: form.studyCode.trim(),
      studyName: resolvedName,
      studyVersion: "",
      channelName: resolvedName,
      channelUrl: form.channelUrl.trim(),
      totalDocs: 0,
      lastSyncedAt: "\u2014",
      syncStatus: "pending",
      syncHealth: "warning",
      selectedFolders: folders,
    };

    setSyncPaths((prev) => [...prev, newPath]);
    setShowChannelModal(false);
    setAddedToast(
      isZh
        ? `已添加 Source，${folders.length} 个路径正在异步校验`
        : `Source added \u2014 ${folders.length} path${folders.length > 1 ? "s" : ""} validating`,
    );
    setTimeout(() => setAddedToast(""), 3000);

    // Trigger async path validation
    runAsyncPathValidation(newPath.id, folders, updateSourceFolders);
  };

  const handleEditChannel = ({
    form,
    folderPaths,
  }: {
    form: { studyCode: string; channelUrl: string };
    folderPaths: { path: string; exclusions: string[] }[];
  }) => {
    if (!editingSource) return;

    const study = mockStudies.find((s) => s.code === form.studyCode);
    const resolvedName = study?.name ?? form.studyCode;

    const updatedFolders: SelectedFolder[] = folderPaths.map((p, idx) => {
      const existing = editingSource.selectedFolders.find((f) => f.name === p.path);
      if (existing) {
        return {
          ...existing,
          exclusions: p.exclusions,
          folderStatus: "active" as const,
          pathValidationStatus: "validating" as const,
          pathValidationError: undefined,
        };
      }
      return {
        id: `new-sf-${Date.now()}-${idx}`,
        name: p.path,
        exclusions: p.exclusions,
        docCount: 0,
        buildStatus: "pending" as const,
        folderStatus: "active" as const,
        pathValidationStatus: "validating" as const,
      };
    });

    setSyncPaths((prev) =>
      prev.map((sp) =>
        sp.id === editingSource.id
          ? {
              ...sp,
              studyCode: form.studyCode.trim(),
              studyName: resolvedName,
              channelName: resolvedName,
              channelUrl: form.channelUrl.trim(),
              selectedFolders: updatedFolders,
            }
          : sp,
      ),
    );
    setEditingSource(null);
    setShowChannelModal(false);
    setAddedToast(
      isZh ? "Source 已更新，路径正在重新校验" : "Source updated \u2014 paths re-validating",
    );
    setTimeout(() => setAddedToast(""), 3000);

    // Trigger async path validation
    runAsyncPathValidation(editingSource.id, updatedFolders, updateSourceFolders);
  };

  const handleDeleteConfirm = () => {
    if (!deletingSource) return;
    setSyncPaths((prev) => prev.filter((p) => p.id !== deletingSource.id));
    setDeletingSource(null);
    setAddedToast(isZh ? "Source 已删除" : "Source deleted");
    setTimeout(() => setAddedToast(""), 3000);
  };

  const openAddForm = (connectionType: "teams" | "sharepoint") => {
    setAddFormConnection(connectionType);
  };

  const handleAddFromForm = (data: MVRSourceFormData) => {
    const folders: SelectedFolder[] = data.includePaths.map((path, idx) => ({
      id: `new-sf-${Date.now()}-${idx}`,
      name: path,
      exclusions: data.excludePaths,
      docCount: 0,
      buildStatus: "pending" as const,
      folderStatus: "active" as const,
      pathValidationStatus: "validating" as const,
    }));

    const study = mockStudies.find((s) => s.code === data.studyCode);
    const resolvedName = study?.name ?? data.studyName ?? data.studyCode;

    const newPath: SyncPathConfig = {
      id: Date.now(),
      studyCode: data.studyCode,
      studyName: resolvedName,
      studyVersion: "",
      channelName: resolvedName,
      channelUrl: data.channelUrl,
      description: data.description || undefined,
      agentTips: data.agentTips || undefined,
      docCategories: data.docCategories.length > 0 ? data.docCategories : undefined,
      docTagGroups: data.docTagGroups.length > 0 ? data.docTagGroups : undefined,
      totalDocs: 0,
      lastSyncedAt: "—",
      syncStatus: "pending",
      syncHealth: "warning",
      selectedFolders: folders,
      visibleToUsers: data.visibleToUsers,
      connectionType: data.connectionType,
      isUserListSource: data.isUserListSource,
    };

    setSyncPaths((prev) => [...prev, newPath]);
    setAddFormConnection(null);
    setAddedToast(
      isZh
        ? `已添加 ${data.connectionType === "teams" ? "Teams Channel" : "SharePoint Site"}，${folders.length} 个路径正在异步校验`
        : `${data.connectionType === "teams" ? "Teams Channel" : "SharePoint Site"} added — ${folders.length} path${folders.length > 1 ? "s" : ""} validating`,
    );
    setTimeout(() => setAddedToast(""), 3000);

    runAsyncPathValidation(newPath.id, folders, updateSourceFolders);
  };

  const openEditModal = (path: SyncPathConfig) => {
    setEditingSource(path);
    setShowChannelModal(true);
  };

  const openDeleteModal = (path: SyncPathConfig) => {
    setDeletingSource(path);
  };

  const filtered = syncPaths.filter((p) => {
    if (filterStudy !== "all" && p.studyCode !== filterStudy) return false;
    if (filterConnection !== "all" && p.connectionType !== filterConnection) return false;
    if (filterDocCategory !== "all" && !(p.docCategories ?? []).includes(filterDocCategory))
      return false;
    if (filterDocTag !== "all" && !(p.docTagGroups ?? []).some((g) => g.tagType === filterDocTag))
      return false;
    if (filterUserListSync === "yes" && !p.isUserListSource) return false;
    if (filterUserListSync === "no" && p.isUserListSource) return false;
    if (filterUserScope === "visible" && !p.visibleToUsers) return false;
    if (filterUserScope === "hidden" && p.visibleToUsers) return false;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const searchable = [p.channelName, p.studyName, p.studyCode, p.channelUrl];
      const matched = searchable.some((v) => (v ?? "").toLowerCase().includes(q));
      if (!matched) return false;
    }

    return true;
  });

  const studyLabel =
    filterStudy === "all"
      ? isZh
        ? "所有研究"
        : "All Studies"
      : (MOCK_STUDIES.find((s) => s.code === filterStudy)?.name ?? filterStudy);

  if (addFormConnection) {
    return (
      <AdminLayout>
        <MVRSourceFormPage
          mode="add"
          connectionType={addFormConnection}
          existingSources={syncPaths}
          onCancel={() => setAddFormConnection(null)}
          onConfirm={handleAddFromForm}
        />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-4 flex items-start justify-between">
        <div>
          <AdminPageTitle
            title={isZh ? "MVR 文档来源" : "MVR Document Sources"}
            tooltip={
              isZh
                ? "管理各研究项目的文档来源配置。添加、编辑或删除 Source，并配置同步文件夹路径。"
                : "Manage document source configurations for each study. Add, edit, or remove sources and configure sync folder paths."
            }
          />
        </div>
        <AddSourceDropdown isZh={isZh} onSelect={openAddForm} />
      </div>

      {/* Auth notice banner */}
      <div
        className="mb-4 flex items-start gap-3 px-4 py-3 rounded-xl border"
        style={{ backgroundColor: "rgba(217,119,6,0.06)", borderColor: "rgba(217,119,6,0.2)" }}
      >
        <div
          className="w-6 h-6 flex items-center justify-center rounded-full shrink-0 mt-0.5"
          style={{ backgroundColor: "rgba(217,119,6,0.12)" }}
        >
          <i className="ri-error-warning-line text-xs" style={{ color: "#b45309" }}></i>
        </div>
        <div>
          <p className="text-xs font-semibold" style={{ color: "#92400e" }}>
            {isZh
              ? "添加 Source 的授权先决条件"
              : "Authorization prerequisite for adding a new source"}
          </p>
          <p className="text-xs mt-0.5 leading-relaxed" style={{ color: "rgba(146,64,14,0.85)" }}>
            {isZh
              ? "在添加新的 SharePoint 来源前，请确认 AZ IT 已对系统的 client_id 完成该 Site 的授权。未经授权的 Site 将无法被索引，文件也无法正常访问。请先联系 AZ IT 完成授权后再继续操作。"
              : "Before adding a new SharePoint source, ensure AZ IT has authorized the system's client_id for that site. Unauthorized sites will fail to be indexed and files will not be accessible. Contact AZ IT to complete the authorization before proceeding."}
          </p>
        </div>
      </div>

      {/* Global Excluded Keywords */}
      <div className="mb-4 p-4 rounded-xl border border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
            {isZh ? "全局排除关键词" : "Global Excluded Keywords"}
            <span className="relative group cursor-help">
              <i className="ri-question-line text-gray-400 text-xs w-4 h-4 flex items-center justify-center"></i>
              <span className="absolute left-full top-1/2 -translate-y-1/2 ml-1.5 hidden group-hover:block w-64 text-[11px] text-gray-600 bg-white border border-gray-200 rounded-md shadow-sm px-2.5 py-1.5 z-50 leading-relaxed">
                {isZh
                  ? "以下关键词将应用于所有 Source 的文档排除"
                  : "These keywords will be applied to all sources for document exclusion"}
              </span>
            </span>
          </span>
          <button
            onClick={() => setShowGlobalKeywords(true)}
            className="text-xs px-3 py-1 rounded-md border border-gray-200 text-gray-600 hover:bg-white cursor-pointer whitespace-nowrap transition-colors"
          >
            <i className="ri-pencil-line mr-1"></i>
            {isZh ? "编辑" : "Edit"}
          </button>
        </div>

        <div>
          {globalKeywords.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {globalKeywords.map((kw, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-1 text-xs text-gray-700 bg-white rounded-md px-2.5 py-1 border border-gray-200"
                >
                  {kw}
                </span>
              ))}
            </div>
          )}
          {globalKeywords.length === 0 && (
            <p className="text-xs text-gray-400 italic">
              {isZh ? "暂无关键词" : "No keywords yet"}
            </p>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        {/* Search */}
        <div className="relative shrink-0">
          <div className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-gray-200 bg-white focus-within:border-[#830051] transition-colors min-w-[200px]">
            <i className="ri-search-line text-gray-400 text-xs"></i>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("common.search")}
              className="flex-1 text-xs outline-none bg-transparent text-gray-700 placeholder-gray-400 min-w-0"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="shrink-0 cursor-pointer w-4 h-4 flex items-center justify-center rounded hover:bg-gray-100"
              >
                <i className="ri-close-line text-gray-400 text-xs"></i>
              </button>
            )}
          </div>
        </div>

        <FilterDropdown label={studyLabel} active={filterStudy !== "all"}>
          <DropdownOption
            label={isZh ? "所有研究" : "All Studies"}
            active={filterStudy === "all"}
            onClick={() => setFilterStudy("all")}
          />
          {MOCK_STUDIES.map((s) => (
            <DropdownOption
              key={s.code}
              label={`${s.name} (${s.code})`}
              active={filterStudy === s.code}
              onClick={() => setFilterStudy(s.code)}
              icon="ri-flask-line"
            />
          ))}
        </FilterDropdown>

        <FilterDropdown
          label={
            filterConnection === "all"
              ? isZh
                ? "连接"
                : "Connection"
              : filterConnection === "teams"
                ? "Teams"
                : "SharePoint"
          }
          active={filterConnection !== "all"}
        >
          <DropdownOption
            label={isZh ? "全部" : "All"}
            active={filterConnection === "all"}
            onClick={() => setFilterConnection("all")}
          />
          <DropdownOption
            label="Teams"
            active={filterConnection === "teams"}
            onClick={() => setFilterConnection("teams")}
          />
          <DropdownOption
            label="SharePoint"
            active={filterConnection === "sharepoint"}
            onClick={() => setFilterConnection("sharepoint")}
          />
        </FilterDropdown>

        <FilterDropdown
          label={filterDocCategory === "all" ? "Doc Categories" : filterDocCategory}
          active={filterDocCategory !== "all"}
        >
          <DropdownOption
            label={isZh ? "全部" : "All"}
            active={filterDocCategory === "all"}
            onClick={() => setFilterDocCategory("all")}
          />
          {allDocCategories.map((cat) => (
            <DropdownOption
              key={cat}
              label={cat}
              active={filterDocCategory === cat}
              onClick={() => setFilterDocCategory(cat)}
            />
          ))}
        </FilterDropdown>

        <FilterDropdown
          label={filterDocTag === "all" ? "Doc Tags" : filterDocTag}
          active={filterDocTag !== "all"}
        >
          <DropdownOption
            label={isZh ? "全部" : "All"}
            active={filterDocTag === "all"}
            onClick={() => setFilterDocTag("all")}
          />
          {allDocTagTypes.map((tag) => (
            <DropdownOption
              key={tag}
              label={tag}
              active={filterDocTag === tag}
              onClick={() => setFilterDocTag(tag)}
            />
          ))}
        </FilterDropdown>

        <FilterDropdown
          label={
            filterUserListSync === "all"
              ? isZh
                ? "用户列表同步"
                : "User List Sync"
              : filterUserListSync === "yes"
                ? isZh
                  ? "是"
                  : "Yes"
                : isZh
                  ? "否"
                  : "No"
          }
          active={filterUserListSync !== "all"}
        >
          <DropdownOption
            label={isZh ? "全部" : "All"}
            active={filterUserListSync === "all"}
            onClick={() => setFilterUserListSync("all")}
          />
          <DropdownOption
            label={isZh ? "是" : "Yes"}
            active={filterUserListSync === "yes"}
            onClick={() => setFilterUserListSync("yes")}
          />
          <DropdownOption
            label={isZh ? "否" : "No"}
            active={filterUserListSync === "no"}
            onClick={() => setFilterUserListSync("no")}
          />
        </FilterDropdown>

        <FilterDropdown
          label={
            filterUserScope === "all"
              ? isZh
                ? "用户可见性"
                : "User Scope"
              : filterUserScope === "visible"
                ? isZh
                  ? "可见"
                  : "Visible"
                : isZh
                  ? "隐藏"
                  : "Hidden"
          }
          active={filterUserScope !== "all"}
        >
          <DropdownOption
            label={isZh ? "全部" : "All"}
            active={filterUserScope === "all"}
            onClick={() => setFilterUserScope("all")}
          />
          <DropdownOption
            label={isZh ? "可见" : "Visible"}
            active={filterUserScope === "visible"}
            onClick={() => setFilterUserScope("visible")}
          />
          <DropdownOption
            label={isZh ? "隐藏" : "Hidden"}
            active={filterUserScope === "hidden"}
            onClick={() => setFilterUserScope("hidden")}
          />
        </FilterDropdown>
        <span className="text-xs text-gray-400 whitespace-nowrap">
          {filtered.length} {isZh ? "个 Source" : "sources"}
        </span>
      </div>

      {/* Table */}
      <div className="border border-gray-200 rounded-2xl bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-max">
            {/* Table Header */}
            <div className="sticky top-0 z-20 flex items-center gap-4 px-5 py-3 bg-gray-50 border-b border-gray-100">
              <div className="flex-none w-[280px]">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {isZh ? "研究名称" : "Study Name"}
                </span>
              </div>
              <div className="flex-none w-[90px]">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {isZh ? "连接" : "Connection"}
                </span>
              </div>
              <div className="flex-none w-[180px]">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  URL
                </span>
              </div>
              <div className="flex-none w-[180px]">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {isZh ? "包含路径" : "Include Paths"}
                </span>
              </div>
              <div className="flex-none w-[140px]">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {isZh ? "排除路径" : "Excluded Paths"}
                </span>
              </div>
              <div className="flex-none w-[140px]">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Doc Categories
                </span>
              </div>
              <div className="flex-none w-[140px]">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Doc Tags
                </span>
              </div>
              <div className="flex-none w-[110px]">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {isZh ? "用户列表同步" : "User List Sync"}
                </span>
              </div>
              <div className="flex-none w-[110px]">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {isZh ? "用户可见性" : "User Scope"}
                </span>
              </div>
              <div className="flex-none w-[90px] sticky right-0 z-30 bg-gray-50 border-l border-gray-100 px-1">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {isZh ? "操作" : "Action"}
                </span>
              </div>
            </div>

            {/* Table Body */}
            <div>
              {filtered.map((path) => (
                <Row
                  key={path.id}
                  path={path}
                  isZh={isZh}
                  onViewUrl={setModalUrl}
                  onEdit={openEditModal}
                  onDelete={openDeleteModal}
                />
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="px-5 py-12 text-center">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-search-line text-xl text-gray-400"></i>
                </div>
                <p className="text-sm font-medium text-gray-500">
                  {isZh ? "没有匹配的 Source" : "No sources found"}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {isZh ? "尝试调整筛选条件" : "Try adjusting filters"}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* URL Modal */}
      {modalUrl && <UrlModal url={modalUrl} isZh={isZh} onClose={() => setModalUrl(null)} />}

      {/* Add/Edit Channel Modal */}
      {showChannelModal && (
        <ChannelModal
          isZh={isZh}
          editingSource={editingSource}
          onConfirm={editingSource ? handleEditChannel : handleAddChannel}
          onCancel={() => {
            setShowChannelModal(false);
            setEditingSource(null);
          }}
        />
      )}

      {/* Delete Confirm Modal */}
      {deletingSource && (
        <DeleteConfirmModal
          isZh={isZh}
          sourceName={deletingSource.channelName}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeletingSource(null)}
        />
      )}

      {/* Global Keywords Modal */}
      {showGlobalKeywords && (
        <GlobalKeywordsModal
          isZh={isZh}
          initialKeywords={globalKeywords}
          onConfirm={(keywords) => {
            setGlobalKeywords(keywords);
            setShowGlobalKeywords(false);
          }}
          onCancel={() => setShowGlobalKeywords(false)}
        />
      )}

      {/* Toast */}
      {addedToast && (
        <div
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl text-white text-sm font-medium"
          style={{ backgroundColor: "#1f2937" }}
        >
          <i className="ri-checkbox-circle-line text-green-400"></i>
          {addedToast}
        </div>
      )}
    </AdminLayout>
  );
}
